/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from "@emotion/react";
import { ConfirmCvsPaymentView } from "features/confirmCvsPayment/ConfirmCvsPaymentView";
import { useI18n } from "hooks/i18n/i18n";
import { useMemo, useState } from "react";

import { convertOrderToConfirmCvsPaymentProps } from "../../utils/convertOrderToConfirmCvsPaymentProps";

import type { LegacyPortalPurchaseHistoryOrder } from "@spwn-portal/grpc";
import type { ReactNode } from "react";

type Input = {
  order: LegacyPortalPurchaseHistoryOrder | null;
};

type Output = {
  openConfirmCvsPayment: () => void;
  renderConfirmCvsPayment: () => ReactNode;
};

/**
 * 未払い注文の支払案内モーダル。購入履歴APIの注文だけから表示値を組む。
 * 表示値は注文から都度導出し、状態としては開閉だけを持つ。
 * 支払案内（コンビニ・キャリア・PayPal）が1つも無い注文では開かない
 */
export const useUnpaidPaymentModalFromApi = (input: Input): Output => {
  const { order } = input;
  const { t } = useI18n();
  const [isOpen, setIsOpen] = useState(false);

  const orderProps = useMemo(() => {
    if (order === null) return null;
    return convertOrderToConfirmCvsPaymentProps({ order, t });
  }, [order, t]);

  const hasPaymentGuide =
    orderProps !== null &&
    (orderProps.cvsInfo !== null ||
      orderProps.phoneInfo !== null ||
      orderProps.paypalInfo !== null);

  const openConfirmCvsPayment = () => {
    if (!hasPaymentGuide) return;
    setIsOpen(true);
  };

  const renderConfirmCvsPayment = (): ReactNode => {
    if (!isOpen || !hasPaymentGuide || orderProps === null) return null;
    return (
      <ConfirmCvsPaymentView
        {...orderProps}
        close={() => setIsOpen(false)}
        isOpen={true}
      />
    );
  };

  return { openConfirmCvsPayment, renderConfirmCvsPayment };
};
